import React, { useContext, useEffect, useState } from 'react';
import { Row, notification } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getMenuId } from '../../utils/helpers';
import CreateUpdateCategoryForm from '../../components/CreateUpdateCategoryForm';

function CategoryCreate({ history }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const { doFetch: createCategoryFetch } = useFetch();
  const { doFetch: uploadCategoryImageFetch } = useFetch();
  const { doFetch: uploadCategoryThumbnailImageFetch } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchCategories } = useFetch();

  // Data states
  const [languageList, setLanguageList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [tagList, setTagList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const menuId = getMenuId();

        if (!menuId) {
          console.error('Menu ID not found');
          setIsLoading(false);
          return;
        }

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Categories
          new Promise((resolve, reject) => {
            fetchCategories({
              url: URLS.getAllCategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ];

        const [languagesData, categoriesData] = await Promise.all(promises);

        // Transform categories data to match expected format
        const transformedCategories = categoriesData.categories.map(cat => ({
          id: cat.id,
          translations: cat.category_translations.map(translation => ({
            lanId: translation.language_id,
            lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
            name: translation.name_text,
          })),
          image: cat.image_url,
          tags: [],
          productsCount: 0,
        }));

        setLanguageList(languagesData);
        setCategoryList(transformedCategories);
        setTagList([]); // Tags not currently used in category creation

        setHeaderComponent('Create New Category');

      } catch (error) {
        console.error('Error loading category create data:', error);
        // Handle error - maybe show error message
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [fetchLanguages, fetchCategories]);

  const uploadCategoryThumbnailImage = async (categoryData, formValues) => {
    if (!formValues.thumbnailImage) {
      notification.success({
        placement: 'bottomRight',
        message: 'Category created successfully.',
        duration: 3,
      });
      // Invalidate categories cache to refetch the list with the new category
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      navigate('/categories');
      return;
    }

    await uploadCategoryThumbnailImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadCategoryThumbnailImage({ categoryId: categoryData.id }),
      params: { image: formValues?.thumbnailImage?.file?.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Category created successfully.',
      onSuccess: () => {
        // Invalidate categories cache to refetch the list with the new category
        queryClient.invalidateQueries({ queryKey: ['categories'] });
        navigate('/categories');
      },
    });
  };

  const uploadCategoryImage = async (categoryData, formValues) => {
    if (!formValues.image) {
      // No main image, try thumbnail image
      uploadCategoryThumbnailImage(categoryData, formValues);
      return;
    }

    await uploadCategoryImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadCategoryImage({ categoryId: categoryData.id }),
      params: { image: formValues?.image?.file?.originFileObj },
      showSuccessNotification: false,
      onSuccess: () => {
        // After main image upload, try thumbnail image
        uploadCategoryThumbnailImage(categoryData, formValues);
      },
    });
  };

  const createNewCategory = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = formValues[`name.${language.id}`];
      return acc;
    }, {});

    await createCategoryFetch({
      url: URLS.createCategory,
      params: { ...translationsObject, tag_ids: formValues.tagIds },
      onSuccess: data => uploadCategoryImage(data, formValues),
    });
  };

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading category creation data...
        </div>
      </Row>
    );
  }

  return (
    <Row>
      <CreateUpdateCategoryForm
        tagList={tagList}
        categoryList={categoryList}
        languageList={languageList}
        onSubmit={createNewCategory}
      />
    </Row>
  );
}

export default CategoryCreate;
